import { toast } from "react-toastify";
import { SmartApplyApiError } from "./../../../application/apis/api-error";
import { SurveyPublishStatus } from "@graffer-inc/cloud-gov-core";
import { BaseApplicationService } from "./../../domain/base/base-application-service";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../domain/pagination/paging";
import { rebuildQuery } from "../../../utils/url";
import { SurveyEntity } from "../../domain/surveys/survey-entity";
import { SurveyImportStatusType } from "../../domain/surveys/survey-import-status";
import { Sort } from "../../domain/pagination/sort";
import { surveyApiClient } from "../../apis/survey-api-client";
import { reportUnexpectedError } from "../../../utils/error-reporter";

export type SurveyListServiceProps = {
  isLoading: boolean;
  paging: {
    current: number;
    total: number;
  };
  queries: {
    sort: Sort;
    name: string;
  };
  surveys: SurveyEntity[];
  selectedStatus: SurveyPublishStatus[];
  onCreateNew: typeof SurveyListService.prototype.onCreateNew;
  onSearch: typeof SurveyListService.prototype.onSearch;
  setPage: typeof SurveyListService.prototype.setPage;
  movePage: typeof SurveyListService.prototype.movePage;
  setQueries: typeof SurveyListService.prototype.setQueries;
  fetchSurveys: typeof SurveyListService.prototype.fetchSurveys;
  setSelectedStatus: typeof SurveyListService.prototype.setSelectedStatus;
  importSurveyFromZipFile: typeof SurveyListService.prototype.importSurveyFromZipFile;
  checkSurveyImportZipFile: typeof SurveyListService.prototype.checkSurveyImportZipFile;
};

export class SurveyListService extends BaseApplicationService<SurveyListServiceProps> {
  private readonly surveyCountPerPage: number = DefaultNumItemsPerPages;

  state = {
    isLoading: false,
    sort: "desc" as const,
    paging: {
      current: 1,
      total: 0,
    },
    queries: {
      sort: "desc" as const,
      name: "",
    },
    surveys: Array<SurveyEntity>(),
    selectedStatus: [],
    groupId: undefined,
    onCreateNew: this.onCreateNew.bind(this),
    onSearch: this.onSearch.bind(this),
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    setQueries: this.setQueries.bind(this),
    fetchSurveys: this.fetchSurveys.bind(this),
    setSelectedStatus: this.setSelectedStatus.bind(this),
    checkSurveyImportZipFile: this.checkSurveyImportZipFile.bind(this),
    importSurveyFromZipFile: this.importSurveyFromZipFile.bind(this),
  };

  async onCreateNew(name: string, groupId: string) {
    this.setIsLoading(true);
    try {
      const res = await surveyApiClient.createSurvey(
        name,
        groupId!,
        SurveyEntity.newFormSchema(),
      );
      this.history.push(`/surveys/surveys/${res.data.id}`);
      toast.success("アンケートを新規に作成しました。");
    } catch (e) {
      reportUnexpectedError(e);
    } finally {
      this.setIsLoading(false);
    }
  }

  onSearch() {
    this.history.push(
      rebuildQuery({
        page: "1",
        q: this.state.queries.name,
      }),
    );
  }

  async fetchSurveys() {
    this.setIsLoading(true);
    try {
      const { data: surveys, headers } = await surveyApiClient.fetchSurveys(
        this.state.paging.current,
        this.surveyCountPerPage,
        {
          query: this.state.queries.name,
          publishStatus:
            this.state.selectedStatus.length > 0
              ? this.state.selectedStatus
              : undefined,
        },
      );
      this.setState({
        paging: {
          ...this.state.paging,
          total: toTotalPage(
            Number(headers["pagination-count"]),
            this.surveyCountPerPage,
          ),
        },
        surveys: surveys,
      });
    } catch (e) {
      reportUnexpectedError(e);
    } finally {
      this.setIsLoading(false);
    }
  }

  async checkSurveyImportZipFile(
    zipData: File,
  ): Promise<SurveyImportStatusType | void> {
    try {
      const { data } =
        await surveyApiClient.checkSurveyImportSerializedFile(zipData);
      return data;
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("インポート用のファイルチェックに失敗しました。");
    }
  }

  async importSurveyFromZipFile(
    zipFile: File,
    groupId: string,
    shallClearCustomPath: boolean,
  ) {
    try {
      const { data } = await surveyApiClient.importSurveyFromZipFile(
        zipFile,
        groupId,
        shallClearCustomPath,
      );
      toast.success(`アンケートをインポートしました。`);
      return data.id;
    } catch (e) {
      if (SmartApplyApiError.isErrorCode(e, "custom_url_already_exists")) {
        toast.error(`URLが重複するため、登録できませんでした。`);
        throw e;
      }
      reportUnexpectedError(e);
      toast.error(`インポートに失敗しました。`);
      throw e;
    }
  }

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  setQueries(
    q: Partial<{
      sort: Sort;
      name: string;
      status: SurveyPublishStatus;
    }>,
  ) {
    this.setState({
      queries: {
        ...this.state.queries,
        ...q,
      },
    });
  }

  setSelectedStatus(status: SurveyPublishStatus[]) {
    this.setState({
      selectedStatus: status,
    });
    this.history.replace(
      rebuildQuery({
        status: status,
      }),
    );
  }

  private setIsLoading(isLoading: boolean) {
    this.setState({
      isLoading: isLoading,
    });
  }
}
